import { ChangeDetectionStrategy, Component, Inject, OnInit } from "@angular/core";
import { ImageCroppedEvent, LoadedImage } from "ngx-image-cropper";
import { take } from "rxjs";
import colors from "wwwroot/json/colors.json";
import icons from "wwwroot/json/icons.json";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { ALLOWED_IMAGE_TYPES, ImgData, TeamAvatarModalData } from "@webapp/teams/models/add-team-avatar-form.models";
import { Team } from "@webapp/teams/models/teams.models";
import { UiModalRef } from "@webapp/ui/modal/abstracts/modal-ref";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";

@Component({
  selector: "add-team-avatar-form",
  templateUrl: "./add-team-avatar-form.component.html",
  styleUrls: ["./add-team-avatar-form.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddTeamAvatarFormComponent implements OnInit {
  public team: Team;
  public icons: string[] = icons;
  public colors: string[] = colors.avatarSelectorColors;
  public allowedImageTypes: string = ALLOWED_IMAGE_TYPES;

  public imgData: ImgData = {
    pictureUrl: "",
    pictureFile: null,
  };

  private croppedImageFile: File | null = null;
  private isUploadMediaAvatarEnabled: boolean = false;

  public constructor(private modalRef: UiModalRef, private featureTogglesFacade: FeatureTogglesFacade, @Inject(UI_MODAL_DATA) private modalData?: { team: Team }) {}

  public ngOnInit(): void {
    if (this.modalData) {
      this.team = this.modalData.team;
    }

    this.featureTogglesFacade
      .isFeatureAvailable$(FeatureFlag.UploadMediaAvatar)
      .pipe(take(1))
      .subscribe((isEnabled) => {
        if (isEnabled) {
          this.isUploadMediaAvatarEnabled = isEnabled;
        }
      });
  }

  public setColor(color: string): void {
    if (this.team) {
      this.team.color = color;
    }
  }

  public setAvatar(avatar: string): void {
    if (this.team) {
      this.team.avatar = avatar;
      this.team.picture = "";
      this.imgData.pictureFile = null;
    }
  }

  public onApply(): void {
    // If there is a croppedImageFile that means the user has cropped the image
    // so we need to use that file instead of the original one
    if (this.croppedImageFile) {
      this.imgData.pictureFile = this.croppedImageFile;
    }

    const modalData: TeamAvatarModalData = {
      imgData: this.imgData,
      team: this.team,
      isUploadMediaAvatarEnabled: this.isUploadMediaAvatarEnabled,
    };

    if (this.modalRef && this.team) {
      this.modalRef.destroy(modalData);
    }
  }

  public closeModal(): void {
    if (this.modalRef) {
      this.modalRef.destroy();
    }
  }

  public fileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files[0];

    if (file) {
      this.imgData.pictureFile = file;
    }
  }

  public imageLoaded(event: LoadedImage): void {
    if (event.original.objectUrl) {
      this.imgData.pictureUrl = event.original.objectUrl;
    }
  }

  public imageCropped(event: ImageCroppedEvent): void {
    const { blob, objectUrl } = event;

    if (blob && objectUrl) {
      // The blob returned by the library is not a File object, so we need to create one
      // Also, that blob doesn't have a name, so we need to set it
      const file = new File([blob], this.imgData.pictureFile.name, { type: blob.type });

      this.croppedImageFile = file;
      this.imgData.pictureUrl = objectUrl;
    }
  }
}
